import React from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux'
import { Card, CardContent, Tooltip, Typography, Grid, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ThemeProvider, StyledEngineProvider, createTheme } from '@mui/material/styles';
import { Message as MessageIcon } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import { getValidationFailureMessages } from '../../../Utils/ValidationFailureFormatter';
import ReactSelect from "../../../../common/components/UI/Select/ReactSelect";
import SideBar from '../../../../common/components/UI/SideBar/SideBar';
import { AdditionalInfo } from '../../../../common-client2/src/components';
import { DataGrid, GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid';
import ViewHistoryButton from './ViewHistoryButton';
import { HistoryPanel } from '../../../App/components/core'
import { ShiftCardWithTooltip } from '../ShiftCardWithTooltip';
import { MTV_CLASSES } from '../managerTableViewClasses';
import { updateFetchSizeOption } from '../actions'
import { RequestStatus, RequestTypes } from '../../../Enums';
import ManagerWarningContent from '../ManagerWarningContent';
import RequestTypeChip from '../RequestTypeChip';
import './RequestTable.scss';
import { getAsLongDate, getAsLongDateTime, formatAs, DateFnsFormats } from '../../../../common-client2/src/services/ukgDateTime';
import { GetModifiedBy } from '../helpers/ManageRequests.helper';
import RequestDetailPanel from '../helpers/RequestDetailPanel';

const WarnTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
    [`& .MuiTooltip-tooltip`]: {
        backgroundColor: 'white',
        color: '#3A3A3A',
        maxWidth: '520px',
        border: '1px solid #dadde9',
    },
});

class RequestTable extends React.Component {
    constructor(props) {
        super(props);
        this.theme = createTheme({
            palette: {
                primary: {
                    main: '#66b3ff',
                },
                secondary: {
                    main: '#66b3ff',
                },
            },

        });
        this.state = {
            openActionOptions: false,
            selectedActionIndex: 0,
            isSidePanelOpen: false,
            historyPanelData: [],
            expandedRowKey: null,
        }
    }

    toggleExpand = (key) => {
        this.setState(prev => ({ expandedRowKey: prev.expandedRowKey === key ? null : key }));
    }

    getChipClass = (rowData, classes) => {
        switch (rowData.actionType) {
            case RequestTypes.toCover:
                return classes.chipTC;
            case RequestTypes.shiftSwap:
                return classes.chipSS;
            case RequestTypes.openShift:
                return classes.chipOS;
            case RequestTypes.voluntaryTimeOff:
                return classes.chipVTO;
            default:
                return '';
        }
    }

    handleSelectFetchSize = (target, value) => {
        this.props.updateFetchSizeOption(value);
    }

    computeRowDisabled = (rowData) => {
        const { activeSubmissionPeriodSettingsWithAwarding, signUpRequestSelected, isNonActiveSignUpRequestSelected } = this.props;
        const isRefusedButOverridable = rowData.status.value === RequestStatus.Refused && rowData.isManualOverrideAllowed;
        return ([RequestStatus.RetractedSubmitted, RequestStatus.Approved].includes(rowData.status.value) ||
            (rowData.status.value === RequestStatus.Refused && !isRefusedButOverridable)) ||
            (signUpRequestSelected && !activeSubmissionPeriodSettingsWithAwarding.includes(rowData.managerSettingAggregateRootId)) ||
            (isNonActiveSignUpRequestSelected && activeSubmissionPeriodSettingsWithAwarding.includes(rowData.managerSettingAggregateRootId));
    }

    toGridColumns = (mtColumns) => mtColumns.filter(Boolean).map((c, idx) => {
        const headerStyle = c.headerStyle || {};
        const widthRaw = headerStyle.width;
        const widthNum = typeof widthRaw === 'string' ? parseInt(widthRaw, 10) : widthRaw;
        const sortable = c.sorting !== false;
        const hasTitle = typeof c.title === 'string' && !!c.title;
        const def = {
            field: c.field || `col_${idx}`,
            headerName: typeof c.title === 'string' ? c.title : '',
            sortable,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                const content = c.render ? c.render(params.row) : params.value;
                return c.cellStyle ? <div style={c.cellStyle}>{content}</div> : content;
            },
        };
        if (hasTitle && !sortable) {
            def.headerClassName = 'mt-static-header';
        }
        if (typeof c.title !== 'string') {
            def.renderHeader = () => c.title;
        }
        if (def.headerName === 'Employees') {
            def.width = 500;
            def.minWidth = 500;
            def.flex = 1;
        } else if (def.headerName === 'Warning') {
            def.width = 110;
        } else if (!hasTitle) {
            def.width = 90;
        } else if (widthNum && widthNum > 0 && widthNum <= 120) {
            def.width = Math.max(widthNum, 100);
        } else if (widthNum && widthNum > 800) {
            def.width = 280;
        } else {
            def.width = 160;
        }
        if (c.customSort) {
            def.valueGetter = (_value, row) => row;
            def.sortComparator = (a, b) => c.customSort(a, b);
        }
        return def;
    });



    handleViewHistoryClick = (rowData) => {
        this.setState({
            isSidePanelOpen: true,
            historyPanelData: rowData.history
        });
    }

    closeSidePanel = () => {
        this.setState({
            isSidePanelOpen: false,
            historyPanelData: []
        });
    }

    render() {
        let { t, data, activeSubmissionPeriodSettingsWithAwarding, signUpRequestSelected, isNonActiveSignUpRequestSelected, fetchResultData, onCheckedRequest, checkedRequests } = this.props;
        const classes = MTV_CLASSES;
        const { isSidePanelOpen, historyPanelData, expandedRowKey } = this.state;
        const baseRowKey = (row) => {
            const idPart = row.requestId != null ? `req-${row.requestId}` : `offer-${row.offerId}`;
            return `${row.actionType}|${idPart}|${row.submissionDate || ''}`;
        };
        const getRowKey = (row) => row.__isDetail__ ? `detail-${row.__parentKey__}` : baseRowKey(row);
        const selectedIds = (checkedRequests || []).map(baseRowKey);

        const rowsWithDetail = [];
        data.forEach(row => {
            rowsWithDetail.push(row);
            const key = baseRowKey(row);
            if (key === expandedRowKey && row.detailPanel) {
                rowsWithDetail.push({ ...row, __isDetail__: true, __parentKey__: key });
            }
        });

        const expandColumn = {
            field: '__expand',
            headerName: '',
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            disableReorder: true,
            width: 50,
            colSpan: (value, row) => row.__isDetail__ ? 999 : 1,
            cellClassName: (params) => params.row.__isDetail__ ? 'detail-cell' : '',
            renderCell: (params) => {
                if (params.row.__isDetail__) {
                    return (
                        <div style={{ width: '100%' }}>
                            <RequestDetailPanel rowData={params.row} classes={classes} />
                        </div>
                    );
                }
                if (!params.row.detailPanel) return null;
                const key = baseRowKey(params.row);
                const isExpanded = expandedRowKey === key;
                return (
                    <FontAwesomeIcon
                        icon={isExpanded ? faChevronUp : faChevronDown}
                        className={classes.plusMinus}
                        size="lg"
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); this.toggleExpand(key); }}
                    />
                );
            },
        };

        const checkboxColumn = { ...GRID_CHECKBOX_SELECTION_COL_DEF };

        const getRequestDate = (rowData) => {
            switch (rowData.actionType) {
                case RequestTypes.toCover:
                case RequestTypes.openShift:
                    return rowData.shift.startDateTime;
                case RequestTypes.shiftSwap:
                    return rowData.startDate;
                case RequestTypes.voluntaryTimeOff:
                    //rowData.startDate: "2023-11-03T00:00:00+00:00" -> Must remove the +00:00
                    const startDateInLocalTz = new Date(rowData.startDate);
                    const localOffset = startDateInLocalTz.getTimezoneOffset();
                    const startDate = new Date(startDateInLocalTz.getTime() + localOffset * 60000);
                    return startDate;
                default:
                    break;
            }
        }

        return (
            <React.Fragment>
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={this.theme}>
                        <DataGrid
                            rows={rowsWithDetail}
                            getRowId={getRowKey}
                            getRowHeight={() => 'auto'}
                            checkboxSelection
                            disableRowSelectionOnClick
                            disableColumnFilter
                            disableColumnSelector
                            isRowSelectable={({ row }) => !row.__isDetail__ && !this.computeRowDisabled(row)}
                            rowSelectionModel={selectedIds}
                            onRowSelectionModelChange={(ids) => {
                                const idSet = new Set(ids);
                                const selectedRows = data.filter(r => idSet.has(baseRowKey(r)));
                                onCheckedRequest(selectedRows);
                            }}
                            localeText={{ noRowsLabel: 'No records to display' }}
                            sx={{
                                backgroundColor: '#FFFFFF',
                                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: '#f0f4f8',
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    backgroundColor: '#f0f4f8',
                                },
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    fontWeight: 900,
                                    fontSize: '14px',
                                },
                                '& .mt-static-header .MuiDataGrid-columnHeaderTitle': {
                                    textDecoration: 'underline',
                                },
                                '& .MuiDataGrid-cell': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingTop: '12px',
                                    paddingBottom: '12px',
                                },
                                '& .MuiDataGrid-cell.detail-cell': {
                                    padding: 0,
                                },
                                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                                    outline: 'none',
                                },
                                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                                    outline: 'none',
                                },
                            }}
                            slots={{
                                toolbar: () => (
                                    <div>
                                        <Grid container alignItems="center">
                                            <Grid item xs={3} md={3}>
                                                <Typography style={{ fontSize: '20px', fontWeight: 500, padding: '16px' }}>
                                                    {selectedIds.length > 0 ? `${selectedIds.length} row(s) selected` : 'Requests'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={7} md={7}></Grid>
                                            <Grid item xs={2} md={2} style={{ textAlign: "right", padding: "7px" }} >
                                                <Grid container>
                                                    <Grid item xs={6} md={6}>
                                                        <Typography className={classes.labelButtons} style={{ paddingTop: "14px" }}>Rows per page: </Typography>
                                                    </Grid>
                                                    <Grid item xs={6} md={6}>
                                                        <ReactSelect
                                                            value={this.props.selectedFetchSize}
                                                            options={this.props.fetchSizeOptions}
                                                            onChange={this.handleSelectFetchSize}
                                                            customStyles={{
                                                                control: styles => ({
                                                                    ...styles,
                                                                    margin: '5px 5px 5px 5px',
                                                                    border: '1px solid #CCCCCC',
                                                                    padding: 3,
                                                                    height: 25,
                                                                    minWidth: 91
                                                                }),
                                                                menu: styles => ({
                                                                    ...styles,
                                                                    zIndex: 100
                                                                })
                                                            }}
                                                            selectWrapperClass={classes.selectWrapper}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </div>
                                ),
                                footer: data.length > 0 ? () => (
                                    <div style={{ textAlign: 'center', padding: '8px', borderTop: '1px solid rgba(224, 224, 224, 1)' }}>
                                        <div>
                                            <Typography className={classes.labelViews}>You've viewed {fetchResultData.viewed} of {fetchResultData.total} requests</Typography>
                                        </div>
                                        <div>
                                            <Button mt={1} hidden={this.props.addMoreDisable} className={classes.buttonAddMore} onClick={() => this.props.addMoreRequests()}>
                                                <Typography className={classes.labelButtons}>Load more requests</Typography>
                                            </Button>
                                        </div>
                                    </div>
                                ) : () => null,
                            }}
                            columns={[expandColumn, checkboxColumn, ...this.toGridColumns([
                                {
                                    title: 'Date submitted',
                                    cellStyle: {
                                        whiteSpace: 'nowrap'
                                    },
                                    render: rowData => {
                                        switch (rowData.actionType) {
                                            case RequestTypes.toCover:
                                            case RequestTypes.openShift:
                                            case RequestTypes.voluntaryTimeOff:
                                                return (
                                                    <React.Fragment>
                                                        {getAsLongDateTime(rowData.requestDate)}
                                                    </React.Fragment>
                                                )
                                            case RequestTypes.shiftSwap:
                                                return (
                                                    <React.Fragment>
                                                        {getAsLongDateTime(rowData.submissionDate)}
                                                    </React.Fragment>
                                                )
                                            default:
                                                break;
                                        }
                                    },
                                    customSort: (a, b) => {
                                        let dateA, dateB;
                                        if (a.actionType === RequestTypes.shiftSwap) {
                                            dateA = formatAs(a.submissionDate, DateFnsFormats.ISO);
                                        }
                                        else {
                                            dateA = formatAs(a.requestDate, DateFnsFormats.ISO);
                                        }

                                        if (b.actionType === RequestTypes.shiftSwap) {
                                            dateB = formatAs(b.submissionDate, DateFnsFormats.ISO);
                                        } else {
                                            dateB = formatAs(b.requestDate, DateFnsFormats.ISO);
                                        }

                                        return dateA < dateB ? -1 : 1
                                    },
                                },
                                {
                                    title: 'Date requested',
                                    cellStyle: {
                                        whiteSpace: 'nowrap'
                                    },
                                    render: rowData => {
                                        return (
                                            <React.Fragment>
                                                {getAsLongDate(getRequestDate(rowData))}
                                            </React.Fragment>
                                        )
                                    },
                                    customSort: (a, b) => {
                                        let dateA = formatAs(getRequestDate(a), DateFnsFormats.ISO);
                                        let dateB = formatAs(getRequestDate(b), DateFnsFormats.ISO);

                                        return dateA < dateB ? -1 : 1
                                    },
                                },
                                {
                                    title: 'Request type',
                                    width: '5',
                                    maxWidth: '5',
                                    render: rowData => (
                                        <React.Fragment>
                                            <RequestTypeChip request={rowData} classes={classes} />
                                        </React.Fragment>
                                    ),
                                    customSort: (a, b) => a.requestType.localeCompare(b.requestType),
                                },
                                {
                                    title: 'Status',
                                    headerStyle: {
                                        width: '115px'
                                    },
                                    sorting: false,
                                    render: rowData => (
                                        <div className="table--status-cell">
                                            <Typography component={'h2'} >
                                                {rowData.status.label}
                                            </Typography>
                                            {
                                                (rowData.messages?.length)
                                                    ? (
                                                        <AdditionalInfo
                                                            icon={<MessageIcon className="message-icon" />}
                                                            placement="auto"
                                                        >
                                                            {
                                                                rowData.messages.map(log => (
                                                                    <div key={`status-cell-message-${log.managerSettingId}-${log?.offerId}-${log?.proposalId}`}>
                                                                        <span className="text-lg-regular">
                                                                            {`${log.employeeName}: "${log.message}"`}
                                                                        </span>
                                                                    </div>
                                                                ))
                                                            }
                                                        </AdditionalInfo>
                                                    )
                                                    : <></>
                                            }
                                        </div>
                                    ),
                                },
                                {
                                    title: 'Employees',
                                    headerStyle: {
                                        width: '1700px'
                                    },
                                    sorting: false,
                                    render: rowData => {
                                        switch (rowData.actionType) {
                                            case RequestTypes.toCover: {
                                                return (
                                                    <React.Fragment>
                                                        <Grid container spacing={4}  >
                                                            <Grid item xs={12} >
                                                                <Typography display='inline' className={classes.employeeNameText}>
                                                                    {`Employee 1 : ${rowData.employeeFrom.name} `}
                                                                </Typography>
                                                                <Typography display='inline' style={{ paddingLeft: '20px' }} className={classes.employeeNameText}>
                                                                    {`Employee 2 : ${rowData.employeeTo.name} `}
                                                                </Typography>
                                                                <ShiftCardWithTooltip
                                                                    shift={rowData.shift}
                                                                    partialShift={rowData.isPartial ? rowData.partialShift : null}
                                                                    actionType={rowData.actionType}
                                                                    timeOffRequest={rowData.timeOffRequest}
                                                                    isDisplaySegmentInfos={true}
                                                                    selectedAssignmentName={rowData.selectedAssignmentName}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </React.Fragment>
                                                );
                                            }
                                            case RequestTypes.shiftSwap:
                                                if (rowData.swapRequestShiftInfo.length <= 1) {
                                                    return (
                                                        <React.Fragment>
                                                            <Grid container spacing={4} >
                                                                <Grid item xs={12} >
                                                                    <Typography className={classes.employeeNameText}>
                                                                        {`Employee 1 : ${rowData.offerorEmployeeName} `}
                                                                    </Typography>
                                                                    <ShiftCardWithTooltip
                                                                        shift={rowData.swapRequestShiftInfo[0].shift}
                                                                        partialShift={rowData.isPartialOffer ? rowData.partialOfferShift : null}
                                                                        actionType={rowData.actionType}
                                                                        isDisplaySegmentInfos={true}
                                                                        selectedAssignmentName={rowData.offeredShiftAssignmentName}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <Typography className={classes.employeeNameText}>
                                                                        {`Employee 2 : ${rowData.swapProposal.proposalEmployeeName} `}
                                                                    </Typography>
                                                                    <ShiftCardWithTooltip
                                                                        shift={rowData.swapProposalShiftInfo[0].shift}
                                                                        partialShift={rowData.isPartialProposal ? rowData.partialProposalShift : null}
                                                                        actionType={rowData.actionType}
                                                                        isDisplaySegmentInfos={true}
                                                                        selectedAssignmentName={rowData.selectedAssignmentName}
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </React.Fragment>
                                                    )
                                                }
                                                else if (rowData.swapRequestShiftInfo.length > 1) {
                                                    return (
                                                        <React.Fragment>
                                                            <Grid container spacing={4} >
                                                                <Grid item xs={12} >
                                                                    <Typography className={classes.employeeNameText}>
                                                                        {`Employee 1 : ${rowData.offerorEmployeeName} `}
                                                                    </Typography>
                                                                    <Button className={classes.transparentButtonNoPadding}>
                                                                        <Card className={classes.SSShiftCard}>
                                                                            <CardContent className={classes.fixPadding}>
                                                                                <Typography style={{ fontSize: '16px', textAlign: 'left', fontWeight: 'bold' }}>
                                                                                    {rowData.swapRequestShiftInfo.length + "  Shifts"}
                                                                                </Typography>
                                                                            </CardContent>
                                                                        </Card>
                                                                    </Button>
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <Typography className={classes.employeeNameText}>
                                                                        {`Employee 2 : ${rowData.swapProposal.proposalEmployeeName} `}
                                                                    </Typography>
                                                                    <Button className={classes.transparentButtonNoPadding}>
                                                                        <Card className={classes.SSShiftCard}>
                                                                            <CardContent className={classes.fixPadding}>
                                                                                <Typography style={{ fontSize: '16px', textAlign: 'left', fontWeight: 'bold' }}>
                                                                                    {rowData.swapProposalShiftInfo.length + "  Shifts"}
                                                                                </Typography>
                                                                            </CardContent>
                                                                        </Card>
                                                                    </Button>
                                                                </Grid>
                                                            </Grid>
                                                        </React.Fragment>
                                                    )
                                                }
                                                break;
                                            case RequestTypes.openShift: {
                                                return (
                                                    <Grid container spacing={4}>
                                                        <Grid item xs={12} >
                                                            <Typography className={classes.employeeNameText}>
                                                                {`Employee: ${rowData.employeeTo.name}`}
                                                            </Typography>
                                                            <ShiftCardWithTooltip
                                                                shift={rowData.shift}
                                                                partialShift={rowData.isPartial ? rowData.partialShift : null}
                                                                actionType={rowData.actionType}
                                                                isApproved={rowData.status.label === RequestStatus.Approved}
                                                                isDisplaySegmentInfos={true}
                                                                selectedAssignmentName={rowData.selectedAssignmentName}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                );
                                            }
                                            case RequestTypes.voluntaryTimeOff: {
                                                let shifts = [];
                                                if (rowData.voluntaryTimeOffType === 2) {
                                                    shifts = rowData.status.value !== RequestStatus.RetractedSubmitted ? rowData.previousShiftsHistory : (rowData.shifts ? rowData.shifts.map(x => x.shift) : []);
                                                } else {
                                                    shifts = rowData.shifts?.map(x => x?.shift) ?? [];
                                                }

                                                return (
                                                    <React.Fragment>
                                                        <Grid container spacing={4} >
                                                            <Grid item xs={12} >
                                                                <Typography className={classes.employeeNameText}>
                                                                    {`Employee: ${rowData.employeeTo.name} `}
                                                                </Typography>
                                                                {(!!shifts && shifts.length === 1)
                                                                    ? (
                                                                    <Typography component="div" display='block'>
                                                                        <ShiftCardWithTooltip
                                                                            shift={shifts[0]}
                                                                            partialShift={rowData.isPartial ? rowData.partialShift : null}
                                                                            actionType={rowData.actionType}
                                                                            isApproved={rowData.status.label === RequestStatus.Approved}
                                                                            isVto={true}
                                                                            isDisplaySegmentInfos={true}
                                                                        />
                                                                    </Typography>)
                                                                    :
                                                                    <Button className={classes.transparentButtonNoPadding}>
                                                                        <Card className={rowData.status.label === RequestStatus.Approved ? classes.VTOShiftCard : classes.VTOShiftCardApproved}>
                                                                            <CardContent className={classes.fixPadding}>
                                                                                <Typography style={{ fontSize: '16px', textAlign: 'left', fontWeight: 'bold' }}>
                                                                                    {(shifts?.length ?? 0) + "  Shifts"}
                                                                                </Typography>
                                                                            </CardContent>
                                                                        </Card>
                                                                    </Button>}
                                                            </Grid>
                                                        </Grid>
                                                    </React.Fragment>
                                                )
                                            }
                                            default:
                                                break;
                                        }
                                    },
                                },
                                {
                                    title: 'Warning',
                                    field: 'warning',
                                    sorting: false,
                                    render: rowData => {

                                        switch (rowData.actionType) {
                                            case RequestTypes.toCover: {
                                                const tcTooltipContent = [];
                                                if (rowData['warnings'].length) {
                                                    rowData['warnings'].forEach((warn, i) => tcTooltipContent.push(<ManagerWarningContent key={`w-${i}`} warning={warn} classes={classes} />));
                                                }
                                                if (rowData.validationFailures?.length > 0) {
                                                    getValidationFailureMessages({ validationFailureDetails: rowData.validationFailures }, this.props.t)
                                                        .forEach((msg, i) => tcTooltipContent.push(<div key={`v-${i}`}>{msg}</div>));
                                                }

                                                return (
                                                    tcTooltipContent.length > 0 ?
                                                        <React.Fragment>
                                                            <WarnTooltip title={tcTooltipContent}>
                                                                <Button className={classes.transparentButton}>
                                                                    <FontAwesomeIcon className={classes.faExclamationTriangle} icon={faExclamationTriangle} />
                                                                </Button>
                                                            </WarnTooltip>
                                                        </React.Fragment>
                                                        :
                                                        null
                                                )
                                            }
                                            case RequestTypes.shiftSwap: {
                                                const mapWarningToJsx = (warn, i) => (<ManagerWarningContent key={`w-${i}`} warning={warn} classes={classes} />);
                                                const filterOutFalsy = (x) => !!x;

                                                const ssFromContent = rowData['warnings']
                                                    ?.filter((warn) => (rowData['offerorEmployeeId'] === warn.employeeId))
                                                    ?.map(mapWarningToJsx)
                                                    ?.filter(filterOutFalsy) || [];

                                                const ssToContent = rowData['warnings']
                                                    ?.filter((warn) => (Number(rowData['swapProposal']['proposalEmployeeIdentifier']) === warn.employeeId))
                                                    ?.map(mapWarningToJsx)
                                                    ?.filter(filterOutFalsy) || [];

                                                if (rowData.validationFailures?.length > 0) {
                                                    getValidationFailureMessages({ validationFailureDetails: rowData.validationFailures }, this.props.t)
                                                        .forEach((msg, i) => {
                                                            const el = <div key={`v-${i}`}>{msg}</div>;
                                                            ssFromContent.push(el);
                                                            ssToContent.push(el);
                                                        });
                                                }

                                                return (
                                                    ssFromContent.length > 0 || ssToContent.length > 0 ?
                                                        <React.Fragment>
                                                            <Grid fixed container spacing={8}  >
                                                                <Grid item xs={12} >
                                                                    {ssFromContent.length > 0 ?
                                                                        <WarnTooltip title={ssFromContent}>
                                                                            <Button className={classes.transparentButton}>
                                                                                <FontAwesomeIcon className={classes.faExclamationTriangle} icon={faExclamationTriangle} />
                                                                            </Button>
                                                                        </WarnTooltip>
                                                                        : null
                                                                    }
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    {ssToContent.length > 0 ?
                                                                        <WarnTooltip title={ssToContent}>
                                                                            <Button className={classes.transparentButton}>
                                                                                <FontAwesomeIcon className={classes.faExclamationTriangle} icon={faExclamationTriangle} />
                                                                            </Button>
                                                                        </WarnTooltip>
                                                                        : null
                                                                    }
                                                                </Grid>
                                                            </Grid>
                                                        </React.Fragment>
                                                        :
                                                        null
                                                )
                                            }
                                            case RequestTypes.openShift: {
                                                const osTooltipContent = [];
                                                if (rowData['warnings'].length) {
                                                    rowData['warnings'].forEach((warn, i) => osTooltipContent.push(<ManagerWarningContent key={`w-${i}`} warning={warn} classes={classes} />));
                                                }
                                                if (rowData.validationFailures?.length > 0) {
                                                    getValidationFailureMessages({ validationFailureDetails: rowData.validationFailures }, this.props.t)
                                                        .forEach((msg, i) => osTooltipContent.push(<div key={`v-${i}`}>{msg}</div>));
                                                }

                                                return (
                                                    osTooltipContent.length > 0 ?
                                                        <React.Fragment>
                                                            <WarnTooltip title={osTooltipContent}>
                                                                <Button className={classes.transparentButton}>
                                                                    <FontAwesomeIcon className={classes.faExclamationTriangle} icon={faExclamationTriangle} />
                                                                </Button>
                                                            </WarnTooltip>
                                                        </React.Fragment>
                                                        :
                                                        null
                                                )
                                            }
                                            case RequestTypes.voluntaryTimeOff:
                                                let warningMsgsVto;
                                                if (rowData['warnings'].length) {
                                                    warningMsgsVto = rowData['warnings'].map(warn => (<ManagerWarningContent warning={warn} classes={classes} />));
                                                }

                                                return (
                                                    warningMsgsVto ?
                                                        <React.Fragment>
                                                            <WarnTooltip
                                                                title={warningMsgsVto}>
                                                                <Button className={classes.transparentButton}>
                                                                    <FontAwesomeIcon className={classes.faExclamationTriangle} icon={faExclamationTriangle} />
                                                                </Button>
                                                            </WarnTooltip>
                                                        </React.Fragment>
                                                        :
                                                        null
                                                )
                                            default:
                                                break;
                                        }
                                    }

                                },
                                {
                                    title: 'Modified by',
                                    field: 'approvedByManagerName',
                                    sorting: false,
                                    render: rowData => {
                                        return (
                                            <React.Fragment>
                                                <Typography>
                                                    {GetModifiedBy(rowData.status.value, rowData.approvedByManagerName)}
                                                </Typography>
                                            </React.Fragment>
                                        )
                                    },
                                },
                                {
                                    sorting: false,
                                    render: rowData => (
                                        <ViewHistoryButton onClick={() => this.handleViewHistoryClick(rowData)} />
                                    ),
                                }
                            ])]}
                        />
                        <SideBar
                            handleDrawerClose={() => this.closeSidePanel()}
                            open={isSidePanelOpen}
                            title={< Typography className={classes.SideBarTitle} > {t('manager.history')}</Typography >}
                        >
                            <HistoryPanel
                                historyResponse={historyPanelData}
                                managerSettingNames={[]}
                            />
                        </SideBar>
                    </ThemeProvider>
                </StyledEngineProvider>
            </React.Fragment>
        );
    }
}

const mapStateToProps = state => {
    return {
        data: state.data,
        selectedFetchSize: state.selectedFetchSize,
        fetchSizeOptions: state.fetchSizeOptions
    }
}
const mapDispatchToProps = {
    updateFetchSizeOption
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation('employmentBillboard')(RequestTable));